import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import {
  MetricBulkRequestCompanyWrapper,
  MetricBulkRequestDto,
  MetricBulkRequestTestEmailDTO,
} from "../../dto/metric-bulk-request";
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { OrganizationSharedModule } from "../../../organization/module/organization-shared-module";
import { filter, takeUntil, tap } from "rxjs/operators";
import { OrganizationsService } from "../../../organization/components/service/organizations.service";
import { Organization } from "../../../organization/dto/Organization";
import {
  CrudKpiMemberResponse,
  KpiModalCompanyMemberCrudComponent,
} from "../kpi-modal-company-member-crud/kpi-modal-company-member-crud.component";
import { ExtendedKpiRecipient, ExtendedKpiRecipientFormGroup, KpiRecipient } from "../../dto/KpiMember";
import { UUID } from "../../../../core/pojo/id-variable";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { firstValueFrom, Subject } from "rxjs";
import { KpiBusinessService } from "../../service/backend/kpi-business.service";
import { NotificationsService } from "../../../../core/service/notifications.service";
import { LocalDateTime } from "aws-sdk/clients/forecastservice";
import { DateService } from "../../../../util/common/date-service/date.service";
import {
  TableGroupIconComponent,
} from "../../../shared/component/icons-deprecated/table-group-icon/table-group-icon.component";

@Component({
  selector: 'app-kpi-modal-bulk-kpi-request',
  standalone: true,
  imports: [
    SharedComponentsModule,
    OrganizationSharedModule,
    KpiModalCompanyMemberCrudComponent,
    TableGroupIconComponent,
  ],
  templateUrl: './kpi-modal-bulk-kpi-request.component.html',
  styleUrl: './kpi-modal-bulk-kpi-request.component.scss',
})
export class KpiModalBulkKpiRequestComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();


  @Input({ required: true }) companyWrappers: MetricBulkRequestCompanyWrapper[];
  @Output() closeEmitter = new EventEmitter<boolean>();

  show = new Show();

  selectedOrg: Organization;

  companiesWrapperFormGroup: KpiRequestCompanyWrapperFormGroup;

  showSendBulkRequestModalWarning = false;

  teamModal = new TeamModalWrapper();
  // if we updated/created/deleted member, this variable is true. It is needed to update all data in the parent on close event.
  isThereAnyChangeToRecipients = false;
  showTreeDotsMenu = false;
  protected readonly DateService = DateService;

  constructor(private orgService: OrganizationsService,
    private notificationService: NotificationsService,
    private kpiService: KpiBusinessService) {
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnChanges(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
  }

  async sendBulkKpiRequest(): Promise<void> {
    const form = this.companiesWrapperFormGroup.value;
    const memberUUIDs = form.companies
      .flatMap((company) => company.members)
      .filter((member) => member.isSelected)
      .map((member) => member.uuid);
    const customText = form.isCustomTextEnabled && form.customText.trim().length > 0 ? form.customText : null;
    const dto: MetricBulkRequestDto = {
      memberUUIDs,
      customText,
    };
    await firstValueFrom(this.kpiService.kpiBulkRequest(dto, this.selectedOrg.uuid));
    const wordEndingForPluralAmount = memberUUIDs.length > 1 ? 's' : '';
    this.notificationService.success({
      title: 'Success',
      message: `Successfully sent email${wordEndingForPluralAmount} to ${memberUUIDs.length} recipient${wordEndingForPluralAmount}`,
    });
    this.closeEmitter.emit(true);
  }

  addMember(company: KpiRequestCompanyFormGroup): void {
    this.teamModal.employee = null;
    this.teamModal.company = { name: company.value.name, uuid: company.value.uuid };
    this.teamModal.isDeleteOperation = false;
    this.teamModal.isModalShown = true;
  }

  editMember(member: ExtendedKpiRecipient, company: KpiRequestCompanyFormGroup): void {
    this.teamModal.employee = member;
    this.teamModal.company = { name: company.value.name, uuid: company.value.uuid };
    this.teamModal.isDeleteOperation = false;
    this.teamModal.isModalShown = true;
  }

  deleteMember(member: ExtendedKpiRecipient, company: KpiRequestCompanyFormGroup): void {
    this.teamModal.employee = member;
    this.teamModal.company = { name: company.value.name, uuid: company.value.uuid };
    this.teamModal.isDeleteOperation = true;
    this.teamModal.isModalShown = true;
  }

  closeEmployeeModal(response: CrudKpiMemberResponse): void {
    this.teamModal.isModalShown = false;
    if (!response) {
      return;
    }
    const company = this.findSelectedCompanyFormGroup();
    const member = { ...response.member, isSelected: !company.getRawValue().isMslActivated };

    switch (response.status) {
      case "create":
        this.addCreatedMemberToCompany(member);
        break;
      case "update":
        this.updateMemberInCompany(member);
        break;
      case "delete":
        this.removeMemberFromCompany(member);
        break;
      default:
        throw new Error('Uncovered path');
    }
    this.isThereAnyChangeToRecipients = true;

    this.subscribeOnFormUpdates();
    this.recalculateControlsAfterChanges(company);
  }

  updateCompanyMemberVisibility(company: KpiRequestCompanyFormGroup): void {
    if (company.value.isAllMembersSelected) {
      // unselect all
      company.controls.members.controls.forEach((member) => member.controls.isSelected.patchValue(false));
      return;
    }
    // select all
    company.controls.members.controls.forEach((member) => member.controls.isSelected.patchValue(true));
    this.recalculateTotalCompaniesAmount();
  }

  showRecipientsTooltip(company: KpiRequestCompanyFormGroup, id: string): void {
    const members = company.getRawValue().members
      .filter((member) => member.isSelected);
    if (members.length === 0) {
      this.show.recipientsTooltip = null;
      return;
    }
    this.show.recipientsTooltip = {
      tooltipCoordinates: document.getElementById(id).getBoundingClientRect(),
      tooltipText: members
        .map((member) => (member.family ? `${member.name} ${member.family}` : member.name))
        .join("\n"),
    };
  }

  async sendTestEmail(): Promise<void> {
    const form = this.companiesWrapperFormGroup.value;
    const email = form.testEmail;
    const customText = form.isCustomTextEnabled && form.customText.trim().length > 0 ? form.customText : null;
    const dto: MetricBulkRequestTestEmailDTO = {
      customText,
      email,
    };
    await firstValueFrom(this.kpiService.kpiBulkRequestTestEmail(dto, this.selectedOrg.uuid));
    this.notificationService.success({ title: 'Success', message: 'Sent test message to ' + dto.email });
  }

  hideSendBulkRequestModal(): void {
    this.showSendBulkRequestModalWarning = false;
  }

  showSendBulkRequestModal(): void {
    this.showSendBulkRequestModalWarning = true;
  }

  private addCreatedMemberToCompany(member: ExtendedKpiRecipient): void {
    const company = this.findSelectedCompanyFormGroup();
    const memberFormGroup = this.createMemberFormGroup(member, company.getRawValue().isMslActivated);
    company.controls.members.push(memberFormGroup);
  }

  private updateMemberInCompany(member: ExtendedKpiRecipient): void {
    const company = this.findSelectedCompanyFormGroup();
    const memberForm = this.findCompanyGroupMemberByUUID(company, member.uuid);
    this.updateMemberValues(memberForm, member);
  }

  private removeMemberFromCompany(member: ExtendedKpiRecipient): void {
    const company = this.findSelectedCompanyFormGroup();
    const memberForm = this.findCompanyGroupMemberByUUID(company, member.uuid);
    const memberPositionIndex = company.controls.members.controls.indexOf(memberForm);
    company.controls.members.removeAt(memberPositionIndex);
  }

  private updateMemberValues(memberForm: ExtendedKpiRecipientFormGroup, member: ExtendedKpiRecipient): void {
    memberForm.controls.name.patchValue(member.name);
    memberForm.controls.family.patchValue(member.family);
    memberForm.controls.mail.patchValue(member.mail);
    memberForm.controls.phone.patchValue(member.phone);
    memberForm.controls.position.patchValue(member.position);
  }

  /**
   * When we want to do a crud operation with a company member,
   * the selected company is saved in {@link teamModal#company} variable.
   * This function searches for this company formGroup.
   *
   * @return the found company form group or null if not found.
   */
  private findSelectedCompanyFormGroup(): KpiRequestCompanyFormGroup {
    const companyGroup = this.companiesWrapperFormGroup.controls
      .companies.controls
      .filter((company) => company.value.uuid === this.teamModal.company.uuid);
    if (companyGroup.length !== 1) {
      return null;
    }
    return companyGroup[0];
  }

  private findCompanyGroupMemberByUUID(company: KpiRequestCompanyFormGroup, uuid: UUID): ExtendedKpiRecipientFormGroup {
    const member = company.controls.members.controls.filter((memberControl) => memberControl.value.uuid === uuid);
    if (member.length !== 1) {
      return null;
    }
    return member[0];
  }

  private initForm(): void {
    const companyFormGroups = this.companyWrappers.map((companyWrapper) => {
      const members = companyWrapper.company.companyMembers
        .map((member) =>
          this.createMemberFormGroup(member, Boolean(companyWrapper.linkForm.getFormRawValue().uuid)));
      return this.createCompanyFormGroup(companyWrapper, members);
    });

    this.companiesWrapperFormGroup = new FormGroup({
      companies: new FormArray(companyFormGroups),
      allCompaniesAmount: new FormControl<number>(0),
      selectedCompaniesAmount: new FormControl<number>(0),
      isCustomTextEnabled: new FormControl<boolean>(false),
      customText: new FormControl<string>(''),
      testEmail: new FormControl<string>('', { validators: [Validators.email, Validators.required] }),
    });

    this.subscribeOnFormUpdates();
    this.recalculateTotalCompaniesAmount();
  }

  private getAllCompaniesAmount(): number {
    return this.companiesWrapperFormGroup.value.companies.length;
  }

  private subscribeOnFormUpdates(): void {
    this._destroyStream.next(); // reset previous subscriptions.
    this.subscribeOnMemberSelection();
  }

  private subscribeOnMemberSelection(): void {
    this.companiesWrapperFormGroup.controls.companies.controls.forEach((company) =>
      company.controls.members.controls.forEach((member) => member.controls.isSelected
        .valueChanges
        .pipe(takeUntil(this._destroyStream))
        .subscribe(() => {
          member.updateValueAndValidity();
          this.recalculateControlsAfterChanges(company);
        })));
  }

  private recalculateControlsAfterChanges(company: KpiRequestCompanyFormGroup): void {
    const amountOfMembersSelected = company.value.members.filter((_member) => _member.isSelected).length;
    company.controls.amountOfMembersSelected.patchValue(amountOfMembersSelected);
    company.controls.isAllMembersSelected.patchValue(amountOfMembersSelected === company.value.members.length);
    company.controls.isAtLeastOneMemberSelected.patchValue(amountOfMembersSelected > 0);
    if (!company.getRawValue().isMslActivated || company.value.members.length === 0) {
      company.controls.isAtLeastOneMemberSelected.disable();
    } else {
      company.controls.isAtLeastOneMemberSelected.enable();
    }
    this.recalculateTotalCompaniesAmount();
  }

  private recalculateTotalCompaniesAmount(): void {
    this.companiesWrapperFormGroup.controls.allCompaniesAmount.patchValue(this.getAllCompaniesAmount());
    this.companiesWrapperFormGroup.controls.selectedCompaniesAmount.patchValue(this.getSelectedCompaniesAmount());
  }

  private createCompanyFormGroup(companyWrapper: MetricBulkRequestCompanyWrapper, members: ExtendedKpiRecipientFormGroup[]): KpiRequestCompanyFormGroup {
    const isMslActivated = Boolean(companyWrapper.linkForm.form.getRawValue().uuid);
    return new FormGroup({
      uuid: new FormControl<string>(companyWrapper.company.uuid),
      isCompanyMemberShown: new FormControl<boolean>(false),
      name: new FormControl<string>(companyWrapper.company.name),
      isMslActivated: new FormControl<boolean>({
        value: Boolean(companyWrapper.linkForm.getFormRawValue().uuid),
        disabled: true, // must be disabled, so the toggle (app-switcher) cannot be clicked.
      }),
      isAtLeastOneMemberSelected: new FormControl<boolean>({
        value: isMslActivated && companyWrapper.company.companyMembers.length > 0,
        disabled: !isMslActivated || companyWrapper.company.companyMembers.length === 0,
      }),
      isAllMembersSelected: new FormControl<boolean>(isMslActivated && companyWrapper.company.companyMembers.length > 0),
      amountOfMembersSelected: new FormControl<number>(isMslActivated ? companyWrapper.company.companyMembers.length : 0),
      members: new FormArray<ExtendedKpiRecipientFormGroup>(members),
      latestNotifications: companyWrapper.latestNotificationData
        ? new FormGroup({
          requestSent: new FormControl<string>(companyWrapper.latestNotificationData.creationTime),
          requestReceived: new FormControl<string>(companyWrapper.latestNotificationData.submissionTime
            ? companyWrapper.latestNotificationData.submissionTime
            : null),
        }) : new FormControl(undefined)
      ,
    });
  }

  private createMemberFormGroup(member: KpiRecipient, isSelectedEnabled: boolean): ExtendedKpiRecipientFormGroup {
    return new FormGroup({
      name: new FormControl<string>(member.name),
      family: new FormControl<string>(member.family),
      companyUUID: new FormControl<string>(member.companyUUID),
      companyName: new FormControl<string>(member.companyName),
      position: new FormControl<string>(member.position),
      mail: new FormControl<string>(member.mail),
      phone: new FormControl<string>(member.phone),
      uuid: new FormControl<string>(member.uuid),
      isSelected: new FormControl<boolean>({ value: isSelectedEnabled, disabled: !isSelectedEnabled }),
    });
  }

  private getSelectedCompaniesAmount(): number {
    return this.companiesWrapperFormGroup.value.companies
      .filter((company) => company.isAtLeastOneMemberSelected)
      .length;
  }

  private initSubscriptions(): void {
    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => this.selectedOrg = org),
        takeUntil(this._destroyStream)
      ).subscribe();
  }
}

class Show {
  recipientsTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };
}

export class KpiRequestCompanyMini {
  uuid: UUID;
  name: string;
}

class TeamModalWrapper {
  isModalShown = false;
  employee: KpiRecipient;
  company: KpiRequestCompanyMini;
  isDeleteOperation: boolean;
}

type LatestNotificationsFormGroup = FormGroup<{
  requestSent: FormControl<LocalDateTime>,
  requestReceived: FormControl<LocalDateTime | null>,
}>

type KpiRequestCompanyFormGroup = FormGroup<{
  uuid: FormControl<string>,
  isCompanyMemberShown: FormControl<boolean>,
  name: FormControl<string>,
  isMslActivated: FormControl<boolean>,
  isAtLeastOneMemberSelected: FormControl<boolean>,
  isAllMembersSelected: FormControl<boolean>,
  amountOfMembersSelected: FormControl<number>,
  members: FormArray<ExtendedKpiRecipientFormGroup>,
  latestNotifications: LatestNotificationsFormGroup | FormControl<undefined>,
}>

type KpiRequestCompanyWrapperFormGroup = FormGroup<{
  companies: FormArray<KpiRequestCompanyFormGroup>,
  selectedCompaniesAmount: FormControl<number>,
  allCompaniesAmount: FormControl<number>,
  isCustomTextEnabled: FormControl<boolean>,
  customText: FormControl<string>
  testEmail: FormControl<string>
}>
